import type { Tour } from '@/types/tour';
import { TourViewer } from '@/components/viewer';
import { Sidebar } from '@/components/sidebar';
import { currentBuildingId, currentFloorId, currentRoomId } from '@/core/state';
import { findRoomLocation, firstRoomLocation } from '@/core/tour-utils';
import { VIEWER_DEFAULTS } from '@/core/config';

// Embed-Modi fuer iframe-Integration:
//
//   ?project=<id>&embed=1               -> 'tour'
//     Vollstaendige Tour, aber ohne App-Header und ohne StartScreen.
//     Sidebar bleibt funktional (Kategorien-Chips + Raumliste).
//
//   ?project=<id>&embed=1&room=<roomId> -> 'pano'
//     Reines 360-Pano dieses Raums, keine Sidebar, kein Header, kein
//     Raumwechsel. Praktisch fuer Detail-Seiten beim Kunden.
//
// Beide Modi setzen body[data-embed='...'] — das CSS in _embed.scss
// blendet die nicht benoetigten Elemente aus.

export type EmbedMode = 'none' | 'tour' | 'pano';

export interface EmbedOptions {
  mode: 'tour' | 'pano';
  // Nur fuer mode='pano' relevant. Wird auch fuer 'tour' beachtet, wenn der
  // Embedder einen Start-Raum vorgeben moechte.
  roomId?: string;
}

export function detectEmbedMode(): { mode: EmbedMode; roomId?: string } {
  const params = new URLSearchParams(window.location.search);
  const embed = params.get('embed');
  const room = params.get('room') ?? undefined;
  if (embed !== '1') return { mode: 'none' };
  return { mode: room ? 'pano' : 'tour', roomId: room };
}

export function mountEmbedViewer(root: HTMLElement, tour: Tour, opts: EmbedOptions): void {
  const requested = opts.roomId ? findRoomLocation(tour, opts.roomId) : undefined;
  const fallback = (tour.defaultRoomId ? findRoomLocation(tour, tour.defaultRoomId) : undefined)
    ?? firstRoomLocation(tour);
  const start = requested ?? fallback;

  if (!start) {
    root.innerHTML = `
      <div class="fatal-error" role="alert">
        <h1>Leere Tour</h1>
        <p>Diese Tour enthaelt noch keine Raeume.</p>
      </div>
    `;
    return;
  }

  // Wenn ein roomId angefragt war, der Raum aber nicht existiert, zeigen
  // wir das explizit — sonst wuerde der Embedder denken, sein Link sei OK.
  if (opts.roomId && !requested) {
    root.innerHTML = `
      <div class="fatal-error" role="alert">
        <h1>Raum nicht gefunden</h1>
        <p>Der angeforderte Raum "${escapeText(opts.roomId)}" existiert in dieser Tour nicht.</p>
      </div>
    `;
    return;
  }

  document.body.dataset.embed = opts.mode;

  // Im Pano-Modus rendert die Sidebar gar nicht — wir brauchen nur den
  // Viewer-Container. Im Tour-Modus bleibt die Sidebar funktional, der
  // Header wird via CSS ausgeblendet (gleicher Grid bleibt, weniger
  // Layout-Aerger als komplettes Umbauen).
  const includeSidebar = opts.mode === 'tour';

  root.innerHTML = `
    ${includeSidebar ? '<aside class="app-sidebar" role="navigation" aria-label="Tour-Navigation"></aside>' : ''}
    <section class="app-viewer" id="viewer" aria-label="360-Grad-Panorama"></section>
  `;
  const sidebarEl = root.querySelector<HTMLElement>('.app-sidebar');
  const viewerEl = root.querySelector<HTMLElement>('#viewer');
  if (!viewerEl) throw new Error('Embed-Skeleton: viewer fehlt.');

  const viewer = new TourViewer(viewerEl, {
    pano: start.room.pano,
    yaw: start.room.initialYaw,
    pitch: start.room.initialPitch ?? VIEWER_DEFAULTS.defaultPitch,
    zoom: start.room.initialZoom ?? VIEWER_DEFAULTS.defaultZoom,
  });

  if (sidebarEl) new Sidebar(sidebarEl, tour);

  if (opts.mode === 'tour') {
    // Wie der Standalone-Viewer: Sidebar-Klick schreibt currentRoomId,
    // wir reagieren mit Pano-Wechsel.
    let isFirstRoomSet = true;
    currentRoomId.subscribe((roomId) => {
      if (isFirstRoomSet) {
        isFirstRoomSet = false;
        return;
      }
      const loc = findRoomLocation(tour, roomId);
      if (!loc) return;
      void viewer.loadPanorama({
        pano: loc.room.pano,
        yaw: loc.room.initialYaw,
        pitch: loc.room.initialPitch ?? VIEWER_DEFAULTS.defaultPitch,
        zoom: loc.room.initialZoom ?? VIEWER_DEFAULTS.defaultZoom,
      });
    });
  }

  currentBuildingId.set(start.building.id);
  currentFloorId.set(start.floor.id);
  currentRoomId.set(start.room.id);
}

function escapeText(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = {
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    };
    return map[c] ?? c;
  });
}
